import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, AlertCircle, ChevronDown, Download } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import axiosInstance from "../../api/axiosInstance";
import { useApp } from "../../context/formflow/AppContext";
import { FieldInput } from "../../components/formflow/FieldInput";
import { ROLE_PALETTE } from "../../constants/formflow/fieldTypes";
import { FIELD_TYPES } from "../../constants/formflow/fieldTypes";
import { getFileUrl } from "../../utils/formflow/helpers";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const num = (v) => parseFloat(v) || 0;

function getRoleColor(roles, roleId) {
  const idx = roles.findIndex((r) => r.id === roleId);
  return ROLE_PALETTE[Math.max(0, idx) % ROLE_PALETTE.length];
}

function isFilled(val) {
  if (val === null || val === undefined) return false;
  if (typeof val === "string") return val.trim().length > 0;
  if (typeof val === "boolean") return true;
  return Boolean(val);
}

function formatDisplayValue(field, value) {
  if (field.type === "checkbox") return value === true || value === "true" ? "✓" : "";
  if (field.type === "date" && value) return new Date(value).toLocaleDateString();
  return value || "";
}

function isServerPrefilledField(field) {
  return isFilled(field?.value);
}

function FillFieldOverlay({ field, roles, isEditable, value, isHighlighted, onSidebarFocus }) {
  const c = getRoleColor(roles, field.roleId);

  if (isServerPrefilledField(field)) {
    const displayValue = formatDisplayValue(field, value);

    return (
      <div
        className="absolute overflow-hidden pointer-events-none"
        style={{
          left: `${num(field.x)}%`,
          top: `${num(field.y)}%`,
          width: `${num(field.w)}%`,
          height: `${num(field.h)}%`,
          zIndex: 2,
        }}
      >
        <div
          className="w-full h-full flex items-center overflow-hidden px-1 text-gray-900 font-medium whitespace-nowrap"
          style={{
            fontSize: `${Math.max(9, field.h * 4)}px`,
            fontFamily: field.type === "signature" || field.type === "initials" ? "cursive" : "inherit",
          }}
        >
          {field.type === "signature" && typeof value === "string" && value.startsWith("data:") ? <img src={value} alt="Signature" className="w-full h-full object-contain pointer-events-none select-none" draggable={false} /> : <span className="truncate">{displayValue}</span>}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => isEditable && onSidebarFocus && onSidebarFocus(field)}
      className="absolute overflow-hidden transition-all duration-150 rounded-sm cursor-pointer"
      style={{
        left: `${num(field.x)}%`,
        top: `${num(field.y)}%`,
        width: `${num(field.w)}%`,
        height: `${num(field.h)}%`,
        border: `${isHighlighted ? 3 : 1}px solid ${isHighlighted ? c.dot : c.border}`,
        opacity: isEditable ? 1 : 0.35,
        boxShadow: isHighlighted ? `0 0 0 5px ${c.dot}55, 0 12px 30px ${c.dot}80` : "none",
        zIndex: isHighlighted ? 20 : 1,
      }}
    >
      {!isEditable ? (
        <div className="w-full h-full flex items-center text-[10px] text-gray-400 px-1 truncate">{field.label}</div>
      ) : (
        <div
          className="
            w-full h-full
            flex items-center
            overflow-hidden px-1
            text-gray-800
            font-medium
            whitespace-nowrap
          "
          style={{
            fontSize: `${Math.max(9, field.h * 4)}px`,
            fontFamily: field.type === "signature" || field.type === "initials" ? "cursive" : "inherit",
            color: value ? "#111827" : "#9ca3af",
          }}
        >
          {field.type === "checkbox" ? value ? <span className="text-green-600 font-bold">✓</span> : null : field.type === "signature" ? value ? <img src={value} alt="Signature" className="w-full h-full object-contain pointer-events-none select-none" draggable={false} /> : <span className="text-gray-400">Sign here</span> : <span className="truncate">{field.type === "date" && value ? new Date(value).toLocaleDateString() : value || field.placeholder || field.label}</span>}
        </div>
      )}
    </div>
  );
}

export function FillFormPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { showToast } = useApp();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [values, setValues] = useState({});
  const [done, setDone] = useState(false);
  const [pages, setPages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [highlightedField, setHighlightedField] = useState(null);
  const [highlightedSidebarField, setHighlightedSidebarField] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [errors, setErrors] = useState({}); // fieldId → true

  const pdfScrollRef = useRef(null);
  const pageRefs = useRef({}); // pageIndex → DOM node
  const fieldRefs = useRef({}); // fieldId → DOM node

  // ── fetch form data ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/api/formflow/sign/${token}`);
        if (!res.data?.success) {
          showToast("Invalid or expired link", "error");
          return navigate("/formflow");
        }
        const form = res.data.form;

        // Normalise field coords to numbers once
        form.fields = form.fields.map((f) => ({
          ...f,
          x: num(f.x),
          y: num(f.y),
          w: num(f.w),
          h: num(f.h),
          required: f.required ?? false,
          hiddenUntilSigned: f.hiddenUntilSigned ?? false,
          value: f.value ?? "",
          readOnly: f.readOnly ?? false,
        }));

        const initialValues = {};
        form.fields.forEach((f) => {
          if (f.value !== undefined && f.value !== null && f.value !== "") {
            initialValues[f.id] = f.value;
          }
        });

        setFormData(form);
        setValues(initialValues);

        if (form.file?.public_url) {
          await loadPdfPages(getFileUrl(form.file.public_url));
        }
      } catch (err) {
        console.error(err);
        showToast("Failed to load document", "error");
        navigate("/formflow");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // ── render PDF pages to canvas images ────────────────────────────────────
  const loadPdfPages = async (url) => {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const imgs = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        imgs.push(canvas.toDataURL());
      }
      setPages(imgs);
    } catch (err) {
      console.error("PDF render error", err);
      showToast("Failed to render PDF", "error");
    }
  };

  // ── value change handler ──────────────────────────────────────────────────
  const handleChange = useCallback((fieldId, val) => {
    setValues((prev) => ({ ...prev, [fieldId]: val }));
    // Clear error for this field once filled
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  // ── scroll PDF panel to a specific field ─────────────────────────────────
  const scrollToField = useCallback((field) => {
    setHighlightedField(field.id);
    setTimeout(() => setHighlightedField(null), 2000);

    // Find the page wrapper and compute approximate scroll position
    const pageNode = pageRefs.current[field.pageIndex];
    const scrollEl = pdfScrollRef.current;
    if (!pageNode || !scrollEl) return;

    const pageTop = pageNode.offsetTop;
    const pageH = pageNode.offsetHeight;
    const fieldH = (field.h / 100) * pageH;
    const targetY = pageTop + (field.y / 100) * pageH + fieldH / 2 - scrollEl.clientHeight / 2;
    scrollEl.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
  }, []);

  // ── scroll sidebar to a specific field ─────────────────────────────────
  const scrollToFieldInSidebar = useCallback((field) => {
    const fieldNode = fieldRefs.current[field.id];
    if (fieldNode) {
      fieldNode.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedSidebarField(field.id);
      setTimeout(() => setHighlightedSidebarField(null), 1500);
    }
  }, []);

  const generateFilledPdf = async () => {
    try {
      const existingPdfBytes = await fetch(getFileUrl(file.public_url)).then((r) => r.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      for (const field of fields) {
        const val = values[field.id];
        if (!isFilled(val)) continue;

        const page = pdfDoc.getPage(field.pageIndex);
        const { width, height } = page.getSize();

        const x = (field.x / 100) * width;
        const y = height - (field.y / 100) * height;
        const fieldWidth = (field.w / 100) * width;
        const fieldHeight = (field.h / 100) * height;
        const fontSize = Math.max(8, fieldHeight * 0.6);

        if (field.type === "checkbox") {
          if (val) {
            page.drawText("✓", {
              x,
              y: y - fieldHeight,
              size: fontSize,
              color: rgb(0, 0.5, 0),
              font,
            });
          }
          continue;
        }

        if (field.type === "signature" && typeof val === "string") {
          try {
            const pngImage = await pdfDoc.embedPng(val);
            page.drawImage(pngImage, { x, y: y - fieldHeight, width: fieldWidth, height: fieldHeight });
          } catch {
            try {
              const jpgImage = await pdfDoc.embedJpg(val);
              page.drawImage(jpgImage, { x, y: y - fieldHeight, width: fieldWidth, height: fieldHeight });
            } catch (e) {
              console.error("Signature image failed", e);
            }
          }
          continue;
        }

        const text = field.type === "date" ? new Date(val).toLocaleDateString() : String(val);
        page.drawText(text, {
          x: x + 2,
          y: y - fieldHeight + fieldHeight * 0.2,
          size: fontSize,
          color: rgb(0, 0, 0),
          font,
          maxWidth: fieldWidth,
        });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      // Build filename: documentName_signed_by_signerName.pdf
      const docName = formData.name || "document";
      const signerName = signer.name.replace(/\s+/g, "_");
      a.download = `${docName}_signed_by_${signerName}.pdf`;
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
      showToast("PDF downloaded successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to generate PDF", "error");
    }
  };

  const handleDownloadPdf = async () => {
    if (!formData) return;
    setDownloading(true);
    await generateFilledPdf();
    setDownloading(false);
  };

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formData) return;
    const myFields = formData.fields.filter((f) => f.roleId === formData.signer.role_id && !f.readOnly && !isServerPrefilledField(f));
    const missingFields = myFields.filter((f) => f.required && !isFilled(values[f.id]));

    if (missingFields.length) {
      const newErrors = {};
      missingFields.forEach((f) => (newErrors[f.id] = true));
      setErrors(newErrors);
      showToast(`Please fill required fields: ${missingFields.map((f) => f.label).join(", ")}`, "error");
      scrollToField(missingFields[0]);
      return;
    }

    try {
      setSubmitting(true);
      const fieldsToSubmit = {};
      myFields.forEach((field) => {
        fieldsToSubmit[field.id] = values[field.id];
      });

      const res = await axiosInstance.post(`/api/formflow/sign/${token}/submit`, {
        fields: fieldsToSubmit,
      });
      if (res.data?.success) {
        setDone(true);
        showToast("Form submitted successfully!", "success");
      } else {
        showToast("Submission failed. Please try again.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Submission failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── loading / empty states ────────────────────────────────────────────────
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 text-sm animate-pulse">Loading document…</div>
      </div>
    );
  if (!formData) return null;

  const { fields, roles, file, signer } = formData;
  const myFields = fields.filter((f) => f.roleId === signer.role_id && !f.readOnly && !isServerPrefilledField(f));
  const visibleDocumentFields = fields.filter((f) => f.roleId === signer.role_id || isServerPrefilledField(f));
  const roleIdx = roles.findIndex((r) => r.id === signer.role_id);
  const colors = ROLE_PALETTE[roleIdx % ROLE_PALETTE.length];

  const filledCount = myFields.filter((f) => isFilled(values[f.id])).length;
  const requiredCount = myFields.filter((f) => f.required).length;
  const requiredFilledCount = myFields.filter((f) => f.required && isFilled(values[f.id])).length;
  const allRequiredFilled = requiredFilledCount === requiredCount;

  // ── "done" screen ─────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 text-green-600 flex items-center justify-center rounded-full">
            <Check size={40} />
          </div>
          <h1 className="text-2xl font-bold mb-2">All done!</h1>
          <p className="text-gray-500 mb-6">
            Your fields have been submitted. Thanks <b>{signer.name}</b>!<br />
            You will receive email with signed document when everyone signs.
          </p>
          {/* <button onClick={() => navigate("/")} className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors">
            Back to Dashboard
          </button> */}
        </div>
      </div>
    );
  }

  // ── main render ───────────────────────────────────────────────────────────
  return (
    <div className="bg-slate-50">
      <div className="min-h-screen bg-gray-50 flex flex-col max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center gap-4 shrink-0 sticky top-0 z-10">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-400 truncate">{file?.original_name}</div>
            <h2 className="text-lg font-bold leading-tight">Document Signing</h2>
          </div>
          <div className="text-sm text-gray-500 shrink-0">
            {signer.name} · {signer.email}
          </div>
        </div>

        {/* Body */}
        <div className="flex gap-6 p-6 flex-1 overflow-hidden max-w-7xl w-full mx-auto">
          {/* PDF Viewer */}
          <div ref={pdfScrollRef} className="flex-1 bg-white shadow-lg overflow-y-auto rounded-lg max-h-[88vh]">
            {pages.length > 0 ? (
              pages.map((src, pageIndex) => (
                <div key={pageIndex} data-page={pageIndex} ref={(el) => (pageRefs.current[pageIndex] = el)} className="relative mb-5">
                  <img src={src} alt={`Page ${pageIndex + 1}`} className="w-full block" />

                  {visibleDocumentFields
                    .filter((f) => f.pageIndex === pageIndex)
                    .map((field) => {
                      const isEditable = field.roleId === signer.role_id && !field.readOnly && !isServerPrefilledField(field);
                      return (
                        <FillFieldOverlay
                          key={field.id}
                          // key={field.id}
                          field={field}
                          roles={roles}
                          isEditable={isEditable}
                          value={values[field.id]}
                          onChange={(val) => handleChange(field.id, val)}
                          isHighlighted={highlightedField === field.id}
                          onSidebarFocus={scrollToFieldInSidebar}
                        />
                      );
                    })}
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Loading PDF preview…</div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-72 shrink-0 flex flex-col gap-4" style={{ height: "calc(100vh - 100px)" }}>
            {/* Progress */}
            <div className="bg-white rounded-xl shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">Progress</span>
                <span className="text-sm text-gray-500">
                  {filledCount}/{myFields.length}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: myFields.length ? `${(filledCount / myFields.length) * 100}%` : "0%",
                    background: colors?.dot ?? "#ef4444",
                  }}
                />
              </div>
              {requiredCount > 0 && (
                <div className="text-xs text-gray-400 mt-1">
                  {requiredFilledCount}/{requiredCount} required filled
                </div>
              )}
            </div>

            {/* Field list */}
            <div className="bg-white p-4 rounded-xl shadow flex-1 min-h-0 flex flex-col">
              <h3 className="font-bold mb-3 text-sm shrink-0">Your Fields ({myFields.length})</h3>
              <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
                {myFields.map((field) => {
                  const ft = FIELD_TYPES.find((t) => t.id === field.type);
                  const filled = isFilled(values[field.id]);
                  const hasError = !!errors[field.id];
                  const isSidebarHighlighted = highlightedSidebarField === field.id;

                  return (
                    <div
                      key={field.id}
                      // key={field.id}
                      ref={(el) => (fieldRefs.current[field.id] = el)}
                      className={`p-3 rounded-lg border transition-all duration-200 ${isSidebarHighlighted ? "ring-2 ring-blue-400 bg-blue-50 border-blue-300" : hasError ? "bg-red-50 border-red-300" : filled ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}
                    >
                      {/* Field header — click to scroll into view */}
                      <button type="button" className="flex items-center gap-2 w-full mb-2 text-left" onClick={() => scrollToField(field)} title="Scroll to field in document">
                        {ft && <ft.Icon size={12} className="text-gray-500 shrink-0" />}
                        <span className="text-sm font-medium flex-1 truncate">{field.label}</span>
                        {field.required && <span className="text-xs text-red-600 font-bold shrink-0">REQ</span>}
                        {hasError && <AlertCircle size={13} className="text-red-500 shrink-0" />}
                        {filled && !hasError && <Check size={14} className="text-green-500 shrink-0" />}
                        <ChevronDown size={12} className="text-gray-400 shrink-0" />
                      </button>

                      {/* Inline input in sidebar (fixed pixel size, not % based) */}
                      <FieldInput field={{ ...field, w: 220, h: field.type === "signature" ? 72 : 36 }} value={values[field.id]} onChange={(val) => handleChange(field.id, val)} colors={colors} />

                      {hasError && <p className="text-xs text-red-500 mt-1">This field is required</p>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={submitting} className={`w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 shadow transition-all ${submitting ? "bg-gray-300 text-gray-500 cursor-not-allowed" : allRequiredFilled || requiredCount === 0 ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}>
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Check size={16} />
                  Submit My Fields
                </>
              )}
            </button>

            {requiredCount > 0 && !allRequiredFilled && (
              <p className="text-xs text-center text-gray-400 -mt-2">
                {requiredCount - requiredFilledCount} required field
                {requiredCount - requiredFilledCount !== 1 ? "s" : ""} remaining
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
